import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Upload, FileSpreadsheet, ArrowLeft, CheckCircle2, AlertCircle,
  Loader2, TrendingUp, TrendingDown, History,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only Pricing Import page.
 *
 * Flow:
 *   1. Drag-drop (or click-to-pick) a .xlsx matching the same schema
 *      as `stickeryou_auto_calculator_v2.xlsx`.
 *   2. UI fires POST /pricing/admin/preview — NO commit. Response
 *      includes a diff against the current live table.
 *   3. Admin reviews the diff (# combos added / removed, # prices
 *      changed, pct range, top 20 changes by magnitude).
 *   4. Click "Commit changes" → POST /pricing/admin/commit. Server
 *      atomically swaps the file + backs up the previous one +
 *      invalidates the engine cache so new prices are live instantly.
 *
 * All actions are admin-gated by the backend (403 otherwise); we
 * additionally hide the page from non-admins at the route level.
 */
const AdminPricingImport = () => {
  const { user, loading } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API_URL}/api/pricing/admin/history?limit=10`)
      .then((r) => setHistory(r.data?.records || []))
      .catch(() => {});
  }, [isAdmin, commitResult]);

  const runPreview = async (fileObj) => {
    setError(null);
    setCommitResult(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append('file', fileObj);
      const { data } = await axios.post(
        `${API_URL}/api/pricing/admin/preview`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPreview(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const runCommit = async () => {
    if (!file || !preview) return;
    setError(null);
    setCommitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/pricing/admin/commit`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setCommitResult(data);
      setPreview(null);
      setFile(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      setError(`Expected .xlsx — got "${f.name}".`);
      return;
    }
    setFile(f);
    runPreview(f);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A7C59]" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-xl shadow p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-1">Admin access required</h1>
          <p className="text-sm text-gray-600 mb-4">
            Pricing imports are restricted to administrators.
          </p>
          <Link to="/splash" className="text-[#4A7C59] underline text-sm">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-[#4A7C59] hover:underline mb-4"
          data-testid="admin-pricing-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin Dashboard
        </Link>

        <div className="bg-white rounded-2xl shadow-md p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileSpreadsheet className="w-7 h-7 text-[#4A7C59]" /> Pricing Import
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Upload an updated <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">.xlsx</code> to refresh live catalog pricing. The previous table is backed up automatically — you can roll back by re-uploading the older file.
              </p>
            </div>
          </div>

          {/* Dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`relative cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition-all ${
              dragOver
                ? 'border-[#4A7C59] bg-[#4A7C59]/5'
                : 'border-gray-300 hover:border-[#4A7C59] hover:bg-gray-50'
            }`}
            data-testid="pricing-import-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-base font-semibold text-gray-900 mb-1">
              {file ? file.name : 'Drop or click to select a .xlsx'}
            </p>
            <p className="text-xs text-gray-500">
              Supports the Auto Calculator format (Stickers, Decals, Iron-Ons, Other tabs).
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mt-5 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2"
              data-testid="pricing-import-error"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div><strong className="font-semibold">Error:</strong> {error}</div>
            </div>
          )}

          {/* Previewing spinner */}
          {previewing && (
            <div className="mt-5 flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Parsing upload…
            </div>
          )}

          {/* Preview */}
          {preview && !committing && (
            <div className="mt-6 border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-[#4A7C59]/5 to-white">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                Preview — {preview.filename}
              </h2>
              <PreviewSummary diff={preview.diff} combos={preview.combos_in_upload} sizes={preview.sizes_in_upload} />
              {preview.diff.price_changes.length > 0 && (
                <PriceChangesTable rows={preview.diff.price_changes} />
              )}
              <div className="mt-5 flex gap-2">
                <Button
                  onClick={runCommit}
                  disabled={committing || preview.diff.total_prices_changed === 0 && preview.diff.combos_added.length === 0 && preview.diff.combos_removed.length === 0}
                  className="bg-[#4A7C59] hover:bg-[#3D6A4A] text-white px-6 py-3 rounded-lg font-semibold"
                  data-testid="pricing-import-commit-btn"
                >
                  {committing ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Committing…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Commit & go live</>
                  )}
                </Button>
                <Button
                  onClick={() => { setPreview(null); setFile(null); }}
                  variant="outline"
                  className="px-6 py-3 rounded-lg"
                >
                  Cancel
                </Button>
              </div>
              {preview.diff.total_prices_changed === 0
                && preview.diff.combos_added.length === 0
                && preview.diff.combos_removed.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  No changes detected — nothing to commit.
                </p>
              )}
            </div>
          )}

          {/* Commit success */}
          {commitResult && (
            <div
              className="mt-6 p-5 rounded-xl bg-green-50 border border-green-200"
              data-testid="pricing-import-success"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-bold text-green-900">Pricing updated — changes are live.</h3>
                  <p className="text-sm text-green-800 mt-1">{commitResult.message}</p>
                  <p className="text-xs text-green-700 mt-2">
                    Previous table backed up to <code className="px-1 py-0.5 bg-green-100 rounded">{commitResult.backup_path?.split('/').pop()}</code>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-3">
                <History className="w-5 h-5 text-gray-500" /> Recent imports
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase">When</th>
                      <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Admin</th>
                      <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase">File</th>
                      <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((rec) => (
                      <tr key={rec.at} className="border-t border-gray-100" data-testid="pricing-history-row">
                        <td className="px-4 py-2 text-gray-600">{new Date(rec.at).toLocaleString()}</td>
                        <td className="px-4 py-2 text-gray-900">{rec.admin_email}</td>
                        <td className="px-4 py-2 text-gray-600 font-mono text-xs">{rec.filename}</td>
                        <td className="px-4 py-2 text-xs">
                          <span className="text-gray-700">{rec.summary?.total_prices_changed ?? 0} prices</span>
                          {rec.summary?.combos_added > 0 && (
                            <span className="ml-2 text-green-700">+{rec.summary.combos_added} new</span>
                          )}
                          {rec.summary?.combos_removed > 0 && (
                            <span className="ml-2 text-red-700">-{rec.summary.combos_removed} removed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewSummary = ({ diff, combos, sizes }) => {
  const { total_prices_changed, combos_added, combos_removed, pct_range } = diff;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
      <Stat label="Combos in upload" value={combos} testId="preview-combos" />
      <Stat label="Distinct sizes" value={sizes} testId="preview-sizes" />
      <Stat
        label="Prices changed"
        value={total_prices_changed}
        tint={total_prices_changed > 0 ? 'amber' : 'gray'}
        testId="preview-changes"
      />
      <Stat
        label="Combos +/-"
        value={`${combos_added.length > 0 ? `+${combos_added.length}` : ''}${combos_removed.length > 0 ? ` -${combos_removed.length}` : ''}`.trim() || '0'}
        tint={combos_added.length || combos_removed.length ? 'amber' : 'gray'}
        testId="preview-combos-diff"
      />
      {pct_range.min !== 0 || pct_range.max !== 0 ? (
        <div className="md:col-span-4 text-xs text-gray-600 flex items-center gap-3">
          <span><strong>% range:</strong> {pct_range.min}% to {pct_range.max}%</span>
          <span>·</span>
          <span><strong>median:</strong> {pct_range.median}%</span>
        </div>
      ) : null}
    </div>
  );
};

const Stat = ({ label, value, tint = 'gray', testId }) => {
  const bg = tint === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-white border-gray-200 text-gray-900';
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${bg}`} data-testid={testId}>
      <div className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
};

const PriceChangesTable = ({ rows }) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-3">
    <table className="w-full text-xs">
      <thead className="bg-gray-50 text-left">
        <tr>
          <th className="px-3 py-2 font-semibold text-gray-600">Combo</th>
          <th className="px-3 py-2 font-semibold text-gray-600">Size</th>
          <th className="px-3 py-2 font-semibold text-gray-600">Qty</th>
          <th className="px-3 py-2 font-semibold text-gray-600 text-right">Old → New</th>
          <th className="px-3 py-2 font-semibold text-gray-600 text-right">Δ %</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.key}-${r.size}-${r.qty}-${i}`} className="border-t border-gray-100" data-testid="preview-change-row">
            <td className="px-3 py-1.5 text-gray-700 font-mono text-[11px]">{r.key}</td>
            <td className="px-3 py-1.5 text-gray-700">{r.size}</td>
            <td className="px-3 py-1.5 text-gray-700">{r.qty}</td>
            <td className="px-3 py-1.5 text-right text-gray-900">
              ${r.old.toFixed(2)} → ${r.new.toFixed(2)}
            </td>
            <td className={`px-3 py-1.5 text-right font-semibold ${r.pct > 0 ? 'text-red-700' : 'text-green-700'}`}>
              <span className="inline-flex items-center gap-1">
                {r.pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {r.pct.toFixed(2)}%
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default AdminPricingImport;
